import { useRedeemBalanceMutation } from '@/src/mutations/earning/redeem-balance'
import { useWalletBalanceQuery } from '@/src/queries/earnings/wallet-balance'
import { User } from '@/src/types/user'
import {
  Text,
  Flex,
  Button,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  ButtonGroup as CBG,
  ButtonGroupProps,
  forwardRef,
  Box,
  Skeleton,
} from '@chakra-ui/react'
import React, { useState } from 'react'
import { toast } from 'sonner'

export const ButtonGroup = forwardRef<ButtonGroupProps, 'div'>((props, ref) => {
  const { isAttached, spacing = 2 } = props
  const styles = {
    flexDirection: 'row',
    '& > *:not(style) ~ *:not(style)': {
      marginStart: spacing,
      marginTop: 0,
    },
  }
  if (!isAttached) {
    return <CBG ref={ref} sx={styles} {...props} />
  }
  return <CBG ref={ref} {...props} />
})

const inactiveStyles = {
  color: 'black',
  bg: 'transparent',
  _hover: { bg: 'transparent' },
}

/**
 * @example (
 * <SelectButtonGroup onChange={handleChangeValue} value={value}>
 *  <Button value={value1}>label1</Button>
 *  <Button value={value2}>label2</Button>
 * </SelectButtonGroup>
 * )
 * @note
 * must have multiple child elements
 */
export const SelectButtonGroup: React.FC<{
  onChange: (value: string) => void
  value: string
  groupProps?: ButtonGroupProps
  children: React.ReactNode
}> = ({ onChange, value, children, groupProps }) => {
  if (!children) throw new Error('Children required')
  // iterate over array of child nodes to apply extended props
  return (
    <ButtonGroup
      border={'solid'}
      borderColor={'#e3e3e3'}
      rounded={100}
      p={0.5}
      {...groupProps}
    >
      {React.Children.map(children as React.ReactElement[], (CHILD) => {
        return React.cloneElement(CHILD, {
          onClick: () => {
            if (value === CHILD?.props?.value) return
            onChange(CHILD?.props?.value)
          },
          ...(value !== CHILD?.props?.value && { sx: inactiveStyles }),
        })
      })}
    </ButtonGroup>
  )
}

export default function Redeem({
  isOpen,
  onClose,
  user,
}: {
  isOpen: boolean
  onClose: () => void
  user: User | null
}) {
  let currency = user?.countryShortname === 'US' ? 'US$' : 'INR'
  const [value, setSelectedValue] = useState<'LYK Coins' | 'INR' | 'US$'>(
    'LYK Coins'
  )
  const [amount, setAmount] = useState<string>('')
  const [successMessage, setSuccessMessage] = useState('')
  const walletBalance = useWalletBalanceQuery()
  const redeemRequest = useRedeemBalanceMutation()
  const handleChange = (value: string) => {
    setSelectedValue(value as 'LYK Coins' | 'INR' | 'US$')
  }
  const [step, setStep] = useState(0)

  const handleModalClose = () => {
    onClose()
    setStep(0)
    setAmount('')
    setSuccessMessage('')
  }

  const handleRedeemBalance = () => {
    if (!amount) {
      toast.error('Please enter an LYK Coins to redeem')
      return
    }
    if (
      (currency === 'US$' && Number(amount) < 1300) ||
      (currency === 'INR' && Number(amount) < 130)
    ) {
      toast.error(
        `Please enter minimum ${currency === 'US$' ? 1300 : 130} LYK Coins to redeem`
      )
      return
    }

    if (!/^\d+(\.\d+)?$/.test(amount)) {
      toast.error('Please enter a valid amount')
      return
    }

    if (
      parseFloat(walletBalance.data?.verified.lykCoin || '0') <
        parseFloat(amount) &&
      value === 'LYK Coins'
    ) {
      toast.error('You do not have enough balance to redeem!')
      return
    }

    if (
      parseFloat(walletBalance.data?.verified.moneyValue || '0') <
        parseFloat(amount) &&
      (value === 'INR' || value === 'US$')
    ) {
      toast.error('You do not have enough balance to redeem!')
      return
    }

    if (parseFloat(walletBalance.data?.verified.lykCoin || '0') < 130) {
      toast.error(
        `Minimum ${currency === 'US$' ? 1300 : 130} LYK Coins are required to redeem!`
      )
      return
    }

    redeemRequest.mutate(
      {
        coinAmount: parseFloat(amount),
        amountType:
          value === 'LYK Coins'
            ? 'lyk-coin'
            : currency === 'US$'
              ? 'usd'
              : 'inr',
      },
      {
        onError: (e) => {
          toast.error(e.message)
        },
        onSuccess: (response: { message?: string }) => {
          if (response?.message) {
            toast.success('Redeem request submitted successfully!')
            setSuccessMessage(response.message)
          }
          setAmount('')
          setStep(1)
        },
      }
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      size={'md'}
      isCentered
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Redeem your rewards</ModalHeader>
        <ModalCloseButton />
        <hr />
        {step === 0 ? (
          <>
            <ModalBody mt={2}>
              <Text mb={2}>Your current balance</Text>
              <Flex mb={4} alignItems={'center'} gap={2}>
                <Image
                  boxSize="35px"
                  src={'/rewards/breakdown/lykcoingreen.svg'}
                  alt="wallet"
                />
                {walletBalance.isPending ? (
                  <Skeleton height="50px" my={3} width="100px"></Skeleton>
                ) : (
                  <>
                    <Text color={'lyk-green'} fontWeight={700} fontSize={32}>
                      {walletBalance.data?.verified.lykCoin || '0'}
                    </Text>
                    <Text color={'lyk-green'} fontWeight={600} fontSize={16}>
                      {'US$'}{' '}
                      {
                        // Math.round(
                        //   parseFloat(
                        walletBalance.data?.verified.moneyValue || '0'
                        //   )
                        // )
                      }
                    </Text>
                  </>
                )}
              </Flex>

              <Text mb={2}>Enter LYK Coins to redeem</Text>

              {/* <Box mb={4}>
                <SelectButtonGroup onChange={handleChange} value={value}>
                  <Button
                    _hover={{ bg: 'lyk-green', color: 'white' }}
                    color={'white'}
                    bg={'lyk-green'}
                    rounded={100}
                    value={'LYK Coins'}
                    fontSize={14}
                    fontWeight={500}
                    width={125}
                  >
                    LYK Coins
                  </Button>
                  <Button
                    _hover={{ bg: 'lyk-green', color: 'white' }}
                    color={'white'}
                    bg={'lyk-green'}
                    rounded={100}
                    value={currency}
                    fontSize={14}
                    fontWeight={500}
                    width={125}
                  >
                    {currency}
                  </Button>
                </SelectButtonGroup>
              </Box> */}

              <Input
                placeholder="Enter LYK Coins"
                size="md"
                type="text"
                mb={2}
                value={amount}
                onChange={(e) => setAmount(String(e.target.value).replace(/[^0-9]/g, ''))}
              />
              <Text mb={5} mt={2} opacity={0.5} fontWeight={500} fontSize={14}>
                * Nominal fees may apply on your first redemption
              </Text>
            </ModalBody>
            <hr />

            <ModalFooter>
              <Flex gap={4} mb={4}>
                <Button
                  px={10}
                  _hover={{ opacity: 0.7 }}
                  size={['sm', 'sm', 'md']}
                  fontWeight={500}
                  onClick={handleModalClose}
                >
                  Cancel
                </Button>

                <Button
                  paddingX={20}
                  bg="lyk-green"
                  color="white"
                  _hover={{ opacity: 0.7 }}
                  size={['sm', 'sm', 'md']}
                  fontWeight={500}
                  isLoading={redeemRequest.isPending}
                  onClick={handleRedeemBalance}
                >
                  Redeem
                </Button>
              </Flex>
              {/* <Button variant="ghost">Secondary Action</Button> */}
            </ModalFooter>
          </>
        ) : (
          <ModalBody my={5}>
            <Box mb={5} display={'flex'} justifyContent={'center'}>
              <Image
                boxSize="75px"
                src={'/rewards/breakdown/greentick.svg'}
                alt="wallet"
              />
            </Box>
            <Text
              mb={4}
              textAlign={'center'}
              fontWeight={600}
              fontSize={24}
              opacity={0.7}
            >
             Redemption successful!
            </Text>

            <Text fontWeight={400} fontSize={16} textAlign={'center'} mb={4}>
              Expect redeemed earnings to show up in your linked bank account
              within 5-7 business days.
            </Text>
            <Text fontWeight={400} fontSize={16} textAlign={'center'} mb={2}>
              Confirmation will be sent to your registered email ID
            </Text>
            <Text fontWeight={400} fontSize={16} textAlign={'center'} mb={2}>
              {successMessage}
            </Text>
          </ModalBody>
        )}
      </ModalContent>
    </Modal>
  )
}
